import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { QueryClient } from '@zg-rentals/react-query5';
import { logError } from '@zg-rentals/log-error';

import {
  Alert,
  Box,
  Button,
  Combobox,
  Form,
  FormField,
  FormHelp,
  Heading,
  Label,
  Modal,
  Text,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';

import { useRentalManagerRenterContactWebApiHandler_getLikelyTenants } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import {
  RentalManagerRenterWebApiHandler_addOrUpdate,
  RentalManagerUserInfoWebApiHandler_get,
  RenterRoleType,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

import { DatarouterError } from '@zg-rentals/web-base';

import TenantForm from '../TenantForm';
import FormFieldLeaseTerm, { LEASE_TERM_OPTIONS } from '../FormFieldLeaseTerm';

import type { Client } from '@zg-rentals/http-client';
import type { TenantFormProps } from '../TenantForm';
import type {
  RentalManagerRenterAddOrUpdateRenterDto,
  RentalManagerRenterAddOrUpdateRentersRequestDto,
  RentalManagerRenterAddOrUpdateRentersResponseDto,
  RenterInfoRenterAddedDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

export type TenantSelectProps = {
  client: Client;
  headerTitle?: string;
  listingAlias: string;
  primaryCTALabel?: string;
  onAddRenterSuccess?: () => void;
  isOpen: boolean;
  queryClient: QueryClient;
  onClose: () => void;
  onPrimaryCTAClick?: (addedRenters?: Array<RenterInfoRenterAddedDto>) => void | undefined;
};

type ComboboxData = {
  value: Array<string>;
  meta: Record<string, unknown>;
  calls: number;
};

type DropdownSelectLeaseTermData = {
  value: string;
  meta: Record<string, unknown>;
  calls: number;
};

const TENANT_SELECT_CONTENT = {
  HEADER_TITLE: 'Add tenants',
  PRIMARY_CTA_LABEL: 'Save',
} as const;

const FORM_DATES_CONTENT = {
  FORM_DATES_TITLE: 'What are your lease terms?',
} as const;

const FORM_SELECT_TENANT_CONTENT = {
  FORM_ADD_TENANTS_TITLE: 'Who are your tenants?',
  LABEL: 'Tenant name',
  FORM_HELP: 'Enter a name to select your tenants. If they are not listed, please add them as a new tenant.',
} as const;

// required for combobox display
const EXTENDED_TENANT_KEYS = { VALUE: 'value', LABEL: 'label' } as const;

const OPTIONS = { LIMIT_TAGS_SHOWN: 5, PLACEHOLDER: 'Select tenants', DEFAULT_RENTER_ID_AS_STRING: '' } as const;

const TOAST_MESSAGE = {
  TENANTS_ADDED: 'Tenants added successfully',
  TENANT_EDITED: 'Tenant details edited successfully',
  TENANT_REMOVED: 'The tenant has been successfully removed',
} as const;

const emptyTenantFormList: RentalManagerRenterAddOrUpdateRenterDto = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  rawEmailAddress: '',
  renterId: OPTIONS.DEFAULT_RENTER_ID_AS_STRING,
  role: {
    type: RenterRoleType.TENANT,
    roleStartDateMs: 0,
    // roleEndDateMs: 0,
  },
};

export const CS_EVENT_TRIGGER_LOCATION = {
  ZRM_PROPERTY_OVERVIEW: 'zrm_property_overview',
  ZRM_POI_ADD_TENANTS_MODAL: 'zrm_poi_add_tenants_modal',
} as const;

const EVENT_IDS = {
  TENANTS_MODULE_SAVE_CLICK: '7909',
} as const;

const getMsFromDate = (date: string) => {
  return date ? new Date(date).getTime() : null;
};

const defaultStartDate = '';
const defaultEndDate = '';

/**
 * @param {TenantSelectProps} props
 *
 * @param {Client} props.client - The client to use for the query
 * @param {string} props.headerTitle - The title of the modal
 * @param {string} props.listingAlias - The listing alias to use for the query
 * @param {string} props.primaryCTALabel - The label of the primary CTA button
 * @param {() => void} props.onAddRenterSuccess - The function to call when the renters are added successfully
 * @param {QueryClient} props.queryClient - The query client to use for the query
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {() => void} props.onClose - The function to call when the modal is closed
 *
 * @returns {JSX.Element}
 */

// error: keys from TenantSelectProps is not assignable to type 'IntrinsicAttributes & object
// TODO: error: Warning: Function components cannot be given refs. Use forwardRef instead.
const ModalTenantSelect = ({
  client,
  headerTitle = TENANT_SELECT_CONTENT.HEADER_TITLE,
  isOpen,
  listingAlias,
  primaryCTALabel = TENANT_SELECT_CONTENT.PRIMARY_CTA_LABEL,
  onAddRenterSuccess,
  queryClient,
  onClose,
  onPrimaryCTAClick = undefined,
}: TenantSelectProps): JSX.Element => {
  const [comboboxData, setComboboxData] = useState<ComboboxData>({ value: [], meta: {}, calls: 0 });
  const [comboboxLeaseTermData, setComboboxLeaseTermData] = useState<DropdownSelectLeaseTermData>({
    value: LEASE_TERM_OPTIONS[0].value,
    meta: {},
    calls: 0,
  });

  const [startDate, setStartDate] = useState<string>(defaultStartDate);
  const [endDate, setEndDate] = useState<string>(defaultEndDate);

  const [serverError, setServerError] = useState<string | null>(null);

  const [formError, setFormError] = useState<Record<string, string>>({
    startDate: '',
    endDate: '',
  });

  const [tenantFormList, setTenantFormList] = useState<Record<string, TenantFormProps>>({});

  const { enqueueToast } = useToast();

  const getLikelyTenantsPostData = { listingAlias };
  const {
    data: getLikelyTenantsData,
    isFetching,
    isLoading,
    error,
  } = useRentalManagerRenterContactWebApiHandler_getLikelyTenants(getLikelyTenantsPostData, {
    httpClient: client,
  });

  const comboboxAddTenantRef = useRef<HTMLDivElement>(null);
  const previousComboboxValueRef = useRef<Array<string>>([]);
  const previousEndDateRef = useRef<string>(defaultEndDate);

  // filter out existing tenants
  const filteredLikelyTenants = useMemo(() => {
    return getLikelyTenantsData?.likelyTenants?.filter((tenant) => tenant.isTenant === false);
  }, [getLikelyTenantsData?.likelyTenants]);

  /**
   * The list of Tenants that have been returned by the getLikelyTenants API
   *
   * These need to be formatted for combobox display
   */
  const formattedLikelyTenants = useMemo(() => {
    // keep track of duplicate labels (fullName)
    const fullNameMap: Array<string> = [];
    let hasDuplicateFullName = false;
    let count = 1;

    return filteredLikelyTenants?.map((tenant) => {
      hasDuplicateFullName = false;

      // we need a unique label in order for remove to work
      if (fullNameMap.includes(tenant?.fullName ?? '')) {
        hasDuplicateFullName = true;
        count++;
      } else {
        fullNameMap.push(tenant?.fullName ?? '');
      }

      return {
        ...tenant,
        [EXTENDED_TENANT_KEYS.VALUE]: tenant.renterId,
        [EXTENDED_TENANT_KEYS.LABEL]: hasDuplicateFullName ? `${tenant.fullName} (${count})` : tenant.fullName,
      };
    });
  }, [filteredLikelyTenants]);

  /**
   * Find a tenant by value from the likely tenants list
   */
  const findTenantByValue = useCallback(
    (value: string) => {
      return formattedLikelyTenants?.find((tenant) => tenant[EXTENDED_TENANT_KEYS.VALUE] === value);
    },
    [formattedLikelyTenants],
  );

  /**
   * Tenants that have been selected from the comboxbox
   */
  const selectedTenantList = useMemo(
    () =>
      comboboxData.value.map((value) => {
        const tenant = { ...findTenantByValue(value) };

        // remove combobox keys
        delete tenant?.[EXTENDED_TENANT_KEYS.VALUE];
        delete tenant?.[EXTENDED_TENANT_KEYS.LABEL];

        return tenant;
      }),
    [comboboxData.value, findTenantByValue],
  );

  // conditional settings
  const placeholder = selectedTenantList.length === 0 ? OPTIONS.PLACEHOLDER : '';

  const getTenantFormValidation = (tenantFormList: Record<string, TenantFormProps>) => {
    const hasFieldError = Object.values(tenantFormList).some((tenantForm) => tenantForm.hasFieldError);

    return hasFieldError;
  };

  const isFixedTermLeaseTerm = comboboxLeaseTermData.value === LEASE_TERM_OPTIONS[0].value;
  const isEndDateRequired = isFixedTermLeaseTerm;

  const isPrimaryCTAButtonDisabled =
    !startDate ||
    (isEndDateRequired && !endDate) ||
    Object.keys(tenantFormList).length === 0 ||
    getTenantFormValidation(tenantFormList) === true;

  /**
   * Invalidate the query when the modal is opened
   */
  useEffect(() => {
    if (isOpen) {
      invalidateQuery();
      clearServerError();
    }

    return () => {
      resetTenantForms();
    };
  }, [isOpen]);

  /**
   * Sync the combobox data to the tenant form list
   */
  useEffect(
    () => {
      const { value: nextValue, meta } = comboboxData;

      const previousValue = previousComboboxValueRef.current;
      const isAddingTenant = nextValue.length > previousValue.length;
      const difference: Array<string> = isAddingTenant
        ? nextValue.filter((value) => !new Set(previousValue).has(value))
        : previousValue.filter((value) => !new Set(nextValue).has(value));

      // update the previous value reference
      previousComboboxValueRef.current = nextValue;

      // handle actions
      if (difference.length === 0) {
        return;
      }

      // handle syncing data change to tenant form list
      if (meta.reason === 'clear button') {
        clearSelectedTenantsFromFormList();

        return;
      }

      const foundTenant = findTenantByValue(difference[0]);

      // no tenant found
      if (!foundTenant) {
        return;
      }

      if (isAddingTenant) {
        const renter = {
          rawEmailAddress: foundTenant?.emailAddress,
          firstName: foundTenant?.firstName,
          lastName: foundTenant?.lastName,
          phoneNumber: foundTenant?.phoneNumber,
          renterId: foundTenant?.renterId,
        } as RentalManagerRenterAddOrUpdateRenterDto;

        addTenantToTenantFormList(renter);
      } else {
        removeTenantFromTenantFormList(foundTenant.renterId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [comboboxData.value, findTenantByValue],
  );

  useEffect(() => {
    // keep a reference to the previous end date
    if (comboboxLeaseTermData.value === LEASE_TERM_OPTIONS[0].value) {
      setEndDate(previousEndDateRef.current);
    }

    // month-to-month lease term
    if (comboboxLeaseTermData.value === LEASE_TERM_OPTIONS[1].value) {
      setEndDate(defaultEndDate);
    }
  }, [comboboxLeaseTermData.value]);

  /**
   * Validate form fields
   */
  useEffect(() => {
    if (endDate && endDate !== defaultEndDate) {
      previousEndDateRef.current = endDate;
    }
  }, [isOpen, startDate, endDate]);

  const clearServerError = useCallback(() => {
    setServerError(null);
  }, []);

  const fetchUser = async () => {
    const response = await RentalManagerUserInfoWebApiHandler_get({}, client).catch((error) => {
      throw error;
    });

    return response;
  };

  /**
   * Clear the selected tenants
   * Does not remove manually added tenants
   */
  const clearSelectedTenantsFromFormList = () => {
    const newTenantFormList = Object.fromEntries(
      Object.entries(tenantFormList).filter(
        ([, value]) =>
          (value.renter as RentalManagerRenterAddOrUpdateRenterDto).renterId === OPTIONS.DEFAULT_RENTER_ID_AS_STRING,
      ),
    );

    setTenantFormList(newTenantFormList);
  };

  const addTenantToTenantFormList = (renter: RentalManagerRenterAddOrUpdateRenterDto | null = null) => {
    const nextId = Object.keys(tenantFormList).length ? Math.max(...Object.keys(tenantFormList).map(Number)) + 1 : 1;

    const tenantFormProps: Partial<TenantFormProps> = {
      id: nextId,
      title: `Tenant #${nextId}`,
      renter: renter || Object.assign({}, emptyTenantFormList),
    };

    setTenantFormList({
      ...tenantFormList,
      [nextId]: tenantFormProps,
    });
  };

  const removeTenantFromTenantFormList = (renterId: string) => {
    const copyTenantFormList = { ...tenantFormList };

    Object.entries(copyTenantFormList).forEach(([key, value]) => {
      if (value.renter?.renterId === renterId) {
        delete copyTenantFormList[key];
      }
    });

    setTenantFormList(copyTenantFormList);
  };

  const resetTenantForms = () => {
    setTenantFormList({});
    setFormError({});
    setStartDate(defaultStartDate);
    setEndDate(defaultEndDate);
    setComboboxData({ value: [], meta: {}, calls: 0 });
    setComboboxLeaseTermData({
      value: LEASE_TERM_OPTIONS[0].value,
      meta: {},
      calls: 0,
    });
  };

  const invalidateQuery = () => {
    queryClient.invalidateQueries({
      queryKey: useRentalManagerRenterContactWebApiHandler_getLikelyTenants.getKey(getLikelyTenantsPostData),
    });
  };

  const handleComboboxClear = () => {
    setComboboxData({ value: [], meta: {}, calls: 0 });
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      resetTenantForms();
      onClose?.();
    }
  };

  const handleComboboxChange = useCallback(
    (value: Array<string>, meta: Record<string, unknown>) => {
      return setComboboxData({ value, meta, calls: comboboxData.calls + 1 });
    },
    [comboboxData.calls],
  );

  const handleComboboxLeaseTermChange = useCallback(
    (value: string, meta: Record<string, unknown>) => {
      return setComboboxLeaseTermData({ value, meta, calls: comboboxLeaseTermData.calls + 1 });
    },
    [comboboxLeaseTermData.calls],
  );

  /**
   * Delete a tenant from the tenant form list
   *
   * @param {number} id - The id of the tenant to delete
   */
  const handleDeleteTenantClick = (id: number) => {
    const copyTenantFormList = { ...tenantFormList };
    const copyComboboxDataValue = [...comboboxData.value];
    const targetRenter = copyTenantFormList[id].renter;
    const { renterId } = targetRenter as RentalManagerRenterAddOrUpdateRenterDto;
    const newComboboxDataValue = copyComboboxDataValue.filter((value) => value !== renterId);

    delete copyTenantFormList[id];

    setTenantFormList(copyTenantFormList);

    // sync combobox data
    setComboboxData({ value: newComboboxDataValue, meta: {}, calls: comboboxData.calls + 1 });
  };

  const handleChange = ({ id, value, name }: { id: number; value: string; name: string; hasFieldError?: boolean }) => {
    const copyTenantFormList = { ...tenantFormList };

    if (copyTenantFormList[id]) {
      // @ts-expect-error
      copyTenantFormList[id]['renter'][name as keyof RentalManagerRenterAddOrUpdateRenterDto] = value;
    }

    setTenantFormList(copyTenantFormList);
  };

  const updateTenantFormListFieldError = ({ id, hasFieldError }: { id: number; hasFieldError: boolean }) => {
    const copyTenantFormList = { ...tenantFormList };

    if (copyTenantFormList[id]) {
      copyTenantFormList[id]['hasFieldError'] = hasFieldError;
    }

    setTenantFormList(copyTenantFormList);
  };

  const addRenters = async (postData: RentalManagerRenterAddOrUpdateRentersRequestDto) => {
    const response = await RentalManagerRenterWebApiHandler_addOrUpdate(postData, client).catch((error) => {
      throw error;
    });

    return response as RentalManagerRenterAddOrUpdateRentersResponseDto;
  };

  const handlePrimaryCTAClick = async () => {
    const response = await fetchUser();

    const { userToken = null } = response ?? {};

    // TODO: remove keys of the optional fields that are empty string
    // TODO: refactor once API changes are committed
    const formattedData: RentalManagerRenterAddOrUpdateRentersRequestDto = {
      listingAlias,
      referenceId: userToken ? `zillow_${userToken}` : 'zillow_0000000000',
      referenceIdType: 'manualEntryId',
      renters: Object.values(tenantFormList).map(({ renter }) => {
        // remove renterId if it is empty
        if (renter.renterId === OPTIONS.DEFAULT_RENTER_ID_AS_STRING) {
          delete renter.renterId;
        }

        // append role data to all items
        renter.role = {
          type: RenterRoleType.TENANT,
          roleStartDateMs: getMsFromDate(startDate) ?? 0,
          roleEndDateMs: !endDate ? undefined : (getMsFromDate(endDate) ?? 0),
        };

        // remove undefined roleEndDateMs
        if (renter.role.roleEndDateMs === undefined) {
          delete renter.role.roleEndDateMs;
        }

        return renter;
      }),
    };

    clearServerError();

    addRenters(formattedData).then(handleAddRentersSuccess).catch(handleAddRentersError);
  };

  const handleAddRentersSuccess = (response: RentalManagerRenterAddOrUpdateRentersResponseDto) => {
    onPrimaryCTAClick?.(response?.rentersAdded);
    onAddRenterSuccess?.();
    handleClose(false);
    enqueueToast(<Toast tone="success">{TOAST_MESSAGE.TENANTS_ADDED}</Toast>);

    // TODO: update getLikelyTenantsData

    return response;
  };

  const handleAddRentersError = (error: DatarouterError) => {
    const displayError = DatarouterError.getDisplayMessage(error);
    let errorMessage = displayError;

    // displayError might only be a string that can not be converted via JSON.parse
    try {
      const parsed = JSON.parse(displayError);

      if (parsed?.error?.message) {
        errorMessage = parsed.error.message;
      }
    } catch {
      // displayError is not JSON, use as is
    }

    const capitalizedError =
      typeof errorMessage === 'string' && errorMessage.length > 0
        ? errorMessage.charAt(0).toUpperCase() + errorMessage.slice(1)
        : 'An unexpected error occurred';

    setServerError(capitalizedError);

    logError({
      error: new Error(`Error adding tenants: ${capitalizedError}`),
      errorType: 'AddOrUpdateRentersError',
      context: 'ModalTenantSelect@handleAddRentersError',
    });
  };

  const handleAddTenantButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    addTenantToTenantFormList(null);
  };

  const renderFormContainer = () => {
    return (
      <Form>
        <Box data-testid="FormDatesContainer">
          <Box>
            <Text textStyle="heading-xs">{FORM_DATES_CONTENT.FORM_DATES_TITLE}</Text>
          </Box>

          <Box
            css={{
              marginBottom: 'tight',
            }}
          />

          <FormFieldLeaseTerm
            startDate={startDate}
            endDate={endDate}
            comboboxLeaseTermData={comboboxLeaseTermData}
            formError={formError}
            isEndDateRequired={isEndDateRequired}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onLeaseTermChange={handleComboboxLeaseTermChange}
            onFormErrorChange={setFormError}
          />
        </Box>

        <Box
          css={{
            marginBottom: 'loose',
          }}
        />

        <Box data-testid="FormAddTenantsContainer">
          <Box
            css={{
              marginBottom: 'tight',
            }}
          >
            <Text textStyle="heading-xs">{FORM_SELECT_TENANT_CONTENT.FORM_ADD_TENANTS_TITLE}</Text>
          </Box>

          <FormField
            className="fs-mask"
            data-testid="TenantName"
            label={<Label required={false}>{FORM_SELECT_TENANT_CONTENT.LABEL}</Label>}
            control={
              <Combobox
                ref={comboboxAddTenantRef}
                value={comboboxData.value}
                showLabelForValue={true}
                // @ts-ignore
                options={formattedLikelyTenants || []}
                fluidDropdown={true}
                isDropdownPortal={true}
                limitTagsShown={OPTIONS.LIMIT_TAGS_SHOWN}
                loading={isFetching || isLoading}
                placeholder={placeholder}
                // @ts-ignore
                onChange={handleComboboxChange}
                onClear={handleComboboxClear}
              />
            }
            description={<FormHelp>{FORM_SELECT_TENANT_CONTENT.FORM_HELP}</FormHelp>}
          />
        </Box>
      </Form>
    );
  };

  const renderBody = () => {
    if (error) {
      return <Text>Failed to load tenant data</Text>;
    }

    return (
      <Box className="fs-unmask" data-testid="ModalTenantSelect">
        {serverError && (
          <>
            <Box
              data-testid="ErrorContainer"
              css={{
                marginBottom: 'loose',
              }}
            >
              <Alert tone="warning">{serverError}</Alert>
            </Box>
          </>
        )}

        {renderFormContainer()}

        <Box data-testid="TenantFormContainer">
          {Object.entries(tenantFormList).length > 0 && (
            <Box
              css={{
                marginBottom: 'tight',
              }}
            />
          )}

          {Object.entries(tenantFormList).map(([key, tenantFormProps], index) => {
            return (
              <Box key={key} css={{ marginBottom: 'loose' }}>
                <TenantForm
                  {...tenantFormProps}
                  title={`Tenant #${index + 1}`}
                  onDeleteTenantClick={handleDeleteTenantClick}
                  onChange={handleChange}
                  onFieldError={updateTenantFormListFieldError}
                />
              </Box>
            );
          })}
        </Box>

        <Box
          css={{
            marginBottom: 'looser',
          }}
        />

        <TextButton textStyle="body-bold" onClick={handleAddTenantButtonClick}>
          + Create new tenant
        </TextButton>
      </Box>
    );
  };

  return (
    <>
      <Modal
        open={isOpen}
        size={'md'}
        scrollDocument={false}
        header={<Heading level={2}>{headerTitle}</Heading>}
        body={renderBody()}
        footer={
          <Button
            data-eventid={EVENT_IDS.TENANTS_MODULE_SAVE_CLICK}
            tone={'brand'}
            disabled={isPrimaryCTAButtonDisabled}
            onClick={handlePrimaryCTAClick}
          >
            {primaryCTALabel}
          </Button>
        }
        onOpenChange={handleClose}
      />
    </>
  );
};

export default memo(ModalTenantSelect);
